import { useState } from "react";
import { Modal, ModalBody, Form, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import { Btn, H2 } from "../../../AbstractElements";
import { toast } from "react-toastify";
import axiosInstance from "../../../Utils/axiosInstance";
import { backendbaseUrl } from "../../../Utils/baseUrl";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { passwordPolicy } from "../../../Helpers/Password.helper";
import "../../../Components/SharedComponents/InputIcons.scss"

interface Props {
    modalOpen: boolean;
    toggle: () => void;
}

export default function ResetPasswordModal({ modalOpen, toggle }: Props): JSX.Element {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const validate = (): boolean => {
        const newErrors = { oldPassword: "", newPassword: "", confirmPassword: "" };
        let valid = true;

        if (!oldPassword) {
            newErrors.oldPassword = "Old password is required";
            valid = false;
        }

        const policyError: string | null = passwordPolicy(newPassword);
        if (!newPassword) {
            newErrors.newPassword = "New password is required";
            valid = false;
        } else if (policyError) {
            newErrors.newPassword = policyError;
            valid = false;
        } else if (newPassword === oldPassword) {
            newErrors.newPassword = "New password must be different from old password";
            valid = false;
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = "Please re-enter your new password";
            valid = false;
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleClose = () => {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({ oldPassword: "", newPassword: "", confirmPassword: "" });
        setShowOld(false);
        setShowNew(false);
        setShowConfirm(false);
        toggle();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const response = await axiosInstance.post(`${backendbaseUrl}/users/change-password/`, {
                old_password: oldPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });

            if (response.status === 200) {
                toast.success("Password changed successfully!");
                handleClose();
            }
        } catch (error: any) {
            if (error.response?.status === 400 || error.response?.status === 401) {
                const data = error.response.data;
                const errorMsg: string = data?.error || data?.message || data?.detail || "";
                if (
                    errorMsg.toLowerCase().includes("old") ||
                    errorMsg.toLowerCase().includes("incorrect") ||
                    errorMsg.toLowerCase().includes("current") ||
                    data?.old_password
                ) {
                    setErrors((prev) => ({
                        ...prev,
                        oldPassword: data?.old_password?.[0] || errorMsg || "Old password is incorrect",
                    }));
                } else if (
                    errorMsg.toLowerCase().includes("new") ||
                    data?.new_password
                ) {
                    setErrors((prev) => ({
                        ...prev,
                        newPassword: data?.new_password?.[0] || errorMsg || "Invalid new password",
                    }));
                } else if (
                    errorMsg.toLowerCase().includes("confirm") ||
                    errorMsg.toLowerCase().includes("match") ||
                    data?.confirm_password
                ) {
                    setErrors((prev) => ({
                        ...prev,
                        confirmPassword: data?.confirm_password?.[0] || errorMsg || "Passwords do not match",
                    }));
                } else {
                    toast.error(errorMsg || "Something went wrong");
                }
            } else {
                toast.error("Network error or server is down");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={modalOpen} toggle={handleClose} centered className="reset-password-modal">
            <ModalBody className="p-4">
                <H2 className="mb-4 text-center" >Reset Your Password</H2>
                <Form onSubmit={handleSubmit} noValidate>

                    {/* Old Password */}
                    <FormGroup>
                        <Label>Old Password</Label>
                        <div className="form-input position-relative">
                            <Input
                                type={showOld ? "text" : "password"}
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                placeholder="Enter your current password"
                                invalid={!!errors.oldPassword}
                                style={{ paddingRight: "44px" }}
                            />
                            <div className="icon-wrapper">
                                <IconButton
                                    onClick={() => setShowOld(!showOld)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    size="small"
                                    aria-label={showOld ? "Hide password" : "Show password"}
                                    className="eye-button"
                                >
                                    {showOld ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </div>
                            <FormFeedback>{errors.oldPassword}</FormFeedback>
                        </div>
                    </FormGroup>

                    {/* New Password */}
                    <FormGroup>
                        <Label>New Password</Label>
                        <div className="form-input position-relative">
                            <Input
                                type={showNew ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter your new password"
                                invalid={!!errors.newPassword}
                                style={{ paddingRight: "44px" }}
                            />
                            <div className="icon-wrapper">
                                <IconButton
                                    onClick={() => setShowNew(!showNew)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    size="small"
                                    aria-label={showNew ? "Hide password" : "Show password"}
                                    className="eye-button"
                                >
                                    {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </div>
                            <FormFeedback>{errors.newPassword}</FormFeedback>
                        </div>
                    </FormGroup>

                    {/* Retype Password */}
                    <FormGroup>
                        <Label>Re-enter Password</Label>
                        <div className="form-input position-relative">
                            <Input
                                type={showConfirm ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter your new password"
                                invalid={!!errors.confirmPassword}
                                style={{ paddingRight: "44px" }}
                            />
                            <div className="icon-wrapper">
                                <IconButton
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    size="small"
                                    aria-label={showConfirm ? "Hide password" : "Show password"}
                                    className="eye-button"
                                >
                                    {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </div>
                            <FormFeedback>{errors.confirmPassword}</FormFeedback>
                        </div>
                    </FormGroup>

                    <div className="d-flex gap-2 mt-3">
                        <Btn color="primary" className="w-100" block disabled={loading} style={{ whiteSpace: "nowrap", height: "48px", fontSize: "14px" }}>
                            {loading ? "Updating..." : "Reset Password"}
                        </Btn>
                        <Btn color="light" className="w-100" block type="button" onClick={handleClose} style={{ whiteSpace: "nowrap", height: "48px", fontSize: "14px" }}>
                            CANCEL
                        </Btn>
                    </div>

                </Form>
            </ModalBody>
        </Modal>
    );
}